/**
 * VibeLineSection — Standalone vibe line / bio section.
 *
 * Section heading with a visible "Show on profile" label + toggle, a labelled
 * textarea with a character counter, and the "Create a Vibe Line" link.
 *
 * @route /profile/edit
 * @parent CommunityProfileSettingsPage
 */

import React from 'react'
import { H2 } from '../../../../../Heading/index'
import EyeHideSolid from '../../../../../Icon/icons/EyeHideSolid/index'
import Button, { TYPES as BUTTON_TYPES } from '../../../../../Button/index'
import ToggleSwitch from '../../../../shared/ToggleSwitch/index'
import { useTranslatedText } from '../../../../../StaticText/StaticTextContext'
import { MAX_BIO_LENGTH } from '../../../../../../services/community/index'
import { TYPE_VIBE_QUIZ } from '../../../../../../services/dialog/index'

const CLS = 'community-profile-settings'

export interface VibeLineSectionProps {
  bio: string
  onBioChange: (value: string) => void
  bioVisibility: string
  onBioVisibilityChange: (value: string) => void
  profileVisibility?: string
  renderModal?: ((type: string, props?: Record<string, unknown>) => void) | null
}

function VibeLineSection ({
  bio,
  onBioChange,
  bioVisibility,
  onBioVisibilityChange,
  profileVisibility = 'public',
  renderModal = null,
}: VibeLineSectionProps) {
  const isStealthMode = profileVisibility === 'private'
  const isBioVisible = !isStealthMode && bioVisibility !== 'private'

  const bioLabelText = useTranslatedText({ keys: ['bioLabel'] })
  const privateProfileText = useTranslatedText({ keys: ['privateProfile'] })
  const showOnProfileText = useTranslatedText({ keys: ['showOnProfile'] })
  const bioSubtitleText = useTranslatedText({ keys: ['bioSubtitle'] })
  const bioPlaceholderText = useTranslatedText({ keys: ['bioPlaceholder'] })
  const bioHintText = useTranslatedText({ keys: ['bioHint'] })

  return (
    <div className={`${CLS}__card ${CLS}__card--bordered ${CLS}__card--vibe`}>
      <div className={`${CLS}__section-heading`}>
        <H2 className={`${CLS}__card-title`}>
          {bioLabelText}
        </H2>
        {isStealthMode ? (
          <span className={`${CLS}__badge ${CLS}__badge--private`}>
            <EyeHideSolid />
            {privateProfileText}
          </span>
        ) : (
          <div className={`${CLS}__toggle-inline-row`}>
            <span className={`${CLS}__toggle-inline-label`}>
              {showOnProfileText}
            </span>
            <ToggleSwitch
              checked={isBioVisible}
              onChange={(checked: boolean) => onBioVisibilityChange(checked ? 'public' : 'private')}
              label={bioLabelText}
              showEyeIcon
            />
          </div>
        )}
      </div>
      <div className={`${CLS}__vibe-input`}>
        <label className={`${CLS}__field-label`} htmlFor="settings-bio">
          {bioLabelText}
          <span className={`${CLS}__field-label-subtitle ${CLS}__field-label-subtitle--inline`}>
            {' - '}
            {bioSubtitleText}
          </span>
        </label>
        <div className={`${CLS}__textarea-wrapper`}>
          <textarea
            id="settings-bio"
            className={`${CLS}__textarea`}
            value={bio}
            onChange={(e) => onBioChange(e.target.value)}
            maxLength={MAX_BIO_LENGTH}
            rows={3}
            placeholder={bioPlaceholderText}
            aria-describedby="settings-bio-count"
          />
          <span
            id="settings-bio-count"
            className={`${CLS}__char-count`}
            aria-live="polite"
          >
            {bio.length}
            /
            {MAX_BIO_LENGTH}
          </span>
        </div>
      </div>
      <Button
        type={BUTTON_TYPES.GHOST}
        className={`${CLS}__vibe-line-link`}
        onClick={() => renderModal && renderModal(TYPE_VIBE_QUIZ, {
          onVibeGenerated: (vibeLine: string) => onBioChange(vibeLine),
        })}
      >
        {bioHintText}
      </Button>
    </div>
  )
}

export default VibeLineSection
